import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { ReactElement } from "react";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { BG, TEXT } from "~/components/theme/colors";
import {
  getAllAuthors,
  getAuthorBySlug,
  type AuthorProfile,
} from "~/lib/authors";
import { getAllPosts, type PostMeta } from "~/lib/mdx";
import { NextPageWithLayout } from "~/pages/_app";

type Props = {
  author: AuthorProfile;
  posts: PostMeta[];
};

const AuthorPage: NextPageWithLayout<Props> = ({ author, posts }) => {
  const baseUrl =
    (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
      /\/+$/,
      ""
    );
  const authorUrl = `${baseUrl}/autorzy/${author.slug}/`;
  const toAbsoluteUrl = (value: string | null) => {
    if (!value) {
      return null;
    }

    if (/^https?:\/\//.test(value)) {
      return value;
    }

    return `${baseUrl}${value.startsWith("/") ? value : `/${value}`}`;
  };
  const authorImageUrl = toAbsoluteUrl(author.image);
  const paragraphs = author.bio
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const profileSchema = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    url: authorUrl,
    mainEntity: {
      "@id": `${authorUrl}#main-author`,
      "@type": "Person",
      identifier: author.slug,
      name: author.name,
      description: author.shortBio,
      jobTitle: author.role,
      url: authorUrl,
      ...(author.sameAs.length > 0 ? { sameAs: author.sameAs } : {}),
      ...(authorImageUrl ? { image: authorImageUrl } : {}),
    },
    hasPart: posts.slice(0, 10).map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      url: `${baseUrl}/poradnik/${post.slug}/`,
      datePublished: post.date,
      dateModified: post.updatedAt,
      author: {
        "@id": `${authorUrl}#main-author`,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(profileSchema),
        }}
      />
      <div className={`${BG.lightMuted} px-4 py-12 sm:py-16 lg:px-6`}>
        <div className="mx-auto max-w-4xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
            Autor
          </p>
          <h1 className={`mb-3 text-3xl font-bold ${TEXT.primary}`}>
            {author.name}
          </h1>
          <p className="text-base font-medium text-sky-800">{author.role}</p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-gray-700">
            {author.shortBio}
          </p>
        </div>
      </div>

      <MarginLayout className="mx-auto max-w-4xl py-12">
        <section className="rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-sky-100/70 p-6 shadow-sm sm:p-8">
          <div className="min-w-0">
            {paragraphs.map((paragraph) => (
              <p
                key={paragraph}
                className="text-sm leading-relaxed text-gray-700 [&:not(:first-child)]:mt-3"
              >
                {paragraph}
              </p>
            ))}

            {author.specialties.length > 0 && (
              <ul className="mt-5 flex flex-wrap gap-2">
                {author.specialties.map((specialty) => (
                  <li
                    key={specialty}
                    className="rounded-full bg-white px-3 py-1 text-xs font-medium text-sky-800 ring-1 ring-sky-100"
                  >
                    {specialty}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {posts.length > 0 && (
          <section className="mt-12 border-t border-gray-200 pt-10">
            <h2 className="mb-6 text-xl font-semibold text-gray-900">
              Artykuły autora
            </h2>
            <ul className="flex flex-col gap-4">
              {posts.map((post) => (
                <li key={post.slug}>
                  <Link
                    href={`/poradnik/${post.slug}/`}
                    className="group flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md"
                  >
                    <span className="inline-block self-start rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                      {post.category}
                    </span>
                    <span className="text-lg font-medium text-gray-900 group-hover:text-sky-800">
                      {post.title}
                    </span>
                    <p className="text-sm leading-relaxed text-gray-600">
                      {post.excerpt}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </MarginLayout>
    </>
  );
};

AuthorPage.getLayout = (page: ReactElement) => {
  const props = (page as React.ReactElement<Props>).props;

  return (
    <MainLayout>
      <NextSeo
        title={`${props.author.name} | Autor Inspektomat`}
        description={`${props.author.role}. ${props.author.shortBio}`}
      />
      {page}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const authors = getAllAuthors();

  return {
    paths: authors.map((author) => ({ params: { slug: author.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = params?.slug as string;
  const author = getAuthorBySlug(slug);

  if (!author) {
    return {
      notFound: true,
    };
  }

  const posts = getAllPosts().filter((post) => post.author === author.name);

  return {
    props: {
      author,
      posts,
    },
  };
};

export default AuthorPage;
